import { useTranslation } from '../../i18n'
import { COLORS } from '../../lib/theme'
import { outerRingCount } from '../../lib/mapBorders'
import { Icon } from '../Icon'
import { PlanHuntAvatar } from './PlanHuntAvatar'
import { StandsMap } from './StandsMap'
import type { GeoArea, GeoPt, MapObject } from '../../lib/types'
import type { MapAppearance } from '../../lib/mapAppearance'
import type { PlanHuntDrive, PlanHuntMember } from './types'

// Stable empty set — the mini-map shows the drive's stands as plain pins (no
// selection rings), so nothing is "selected".
const EMPTY_SELECTION: ReadonlySet<string> = new Set()

export function PlanHuntDriveRow({
  drive,
  index,
  total,
  teamPins,
  boundaryAreas = [],
  subAreas = [],
  forbiddenPolygons = [],
  appearance,
  teamBorderColor,
  participants,
  onEdit,
  onRemove,
  onMove,
}: {
  drive: PlanHuntDrive
  index: number
  total: number
  teamPins: MapObject[]
  /** Every parcel of the ground, plus its sub-areas and no-hunting zones. */
  boundaryAreas?: readonly GeoArea[]
  forbiddenPolygons?: readonly GeoArea[]
  appearance: MapAppearance
  teamBorderColor?: string
  /** Team-map sub-areas, to resolve the drive's base name and mini-map outline. */
  subAreas?: GeoArea[]
  participants: PlanHuntMember[]
  onEdit: () => void
  onRemove: () => void
  onMove: (dir: number) => void
}) {
  const { t } = useTranslation('planHuntDrives')
  const stands = drive.stands || []
  // The drive's base is the MULTI-SELECT's `subAreaIds` — the editor's source
  // of truth since the single-select area picker was retired. An empty list
  // means the entire hunting ground (the editor's own implicit default), and a
  // stale id (removed from the team map since this drive was last edited)
  // contributes nothing, matching the editor's own fallback for the same case.
  const subAreaIds = drive.subAreaIds ?? []
  const selectedSubAreas = subAreaIds
    .map((id) => subAreas.find((a) => a.id === id))
    .filter((a): a is GeoArea => a !== undefined)
  const subAreaName = (area: GeoArea) =>
    area.name.trim() || t('row.unnamedSubArea', { number: subAreas.indexOf(area) + 1 })
  const baseName =
    selectedSubAreas.length === 0
      ? t('row.entireGround')
      : selectedSubAreas.length === 1
        ? subAreaName(selectedSubAreas[0])
        : t('row.subAreaCount', { count: selectedSubAreas.length })
  // The mini-map highlight for every selected sub-area, not just one: each
  // area's own outer rings first (across all of them), then each area's own
  // holes — the same flat rings+outerCount shape `StandsMap`'s highlight
  // already accepts and resolves through the shared union pipeline (see its
  // file doc comment), so two touching selections merge into one outline and
  // two disjoint ones draw as separate parts, with each hole reattached to
  // whichever outer actually contains it (by overlap, not by list order).
  const highlightRings: GeoPt[][] = [
    ...selectedSubAreas.flatMap((a) => a.rings.slice(0, outerRingCount(a))),
    ...selectedSubAreas.flatMap((a) => a.rings.slice(outerRingCount(a))),
  ]
  const highlightOuterCount = selectedSubAreas.reduce((sum, a) => sum + outerRingCount(a), 0)
  const selectedIds = new Set(
    stands.map((s) => s.pinId).filter((id): id is string => id !== null),
  )
  const drivePins = teamPins.filter((p) => selectedIds.has(p.id))
  const assignedCount = stands.filter((s) => s.assignedId).length
  // Distinct assigned hunters, in stand order — a face pile, deduped so a hunter
  // covering two stands shows once (and keys stay unique).
  const assignees: PlanHuntMember[] = []
  const seenHunters = new Set<string>()
  for (const s of stands) {
    if (!s.assignedId || seenHunters.has(s.assignedId)) continue
    const member = participants.find((p) => p.id === s.assignedId)
    if (member) {
      seenHunters.add(s.assignedId)
      assignees.push(member)
    }
  }
  const noStands = stands.length === 0
  const nameMissing = drive.name.trim().length === 0

  return (
    <div
      className="wh-plan-drive-row"
      style={{
        display: 'grid',
        gridTemplateColumns: '120px 1fr auto',
        gap: 12,
        alignItems: 'stretch',
        padding: 10,
        borderRadius: 10,
        border: `1px solid ${COLORS.surfaceContainerHighest}`,
        background: COLORS.surfaceContainerLowest,
      }}
    >
      {/* Mini overview map */}
      <button
        onClick={onEdit}
        title={t('row.editDrive')}
        className="wh-plan-drive-row-map"
        style={{
          position: 'relative', width: 120, height: 86, borderRadius: 8, overflow: 'hidden',
          border: `1px solid ${COLORS.surfaceContainerHighest}`, padding: 0, cursor: 'pointer',
          background: COLORS.surface,
        }}
      >
        {noStands ? (
          <div
            style={{
              width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, color: COLORS.outline, textAlign: 'center', padding: 6,
            }}
          >
            {t('row.noStandsYet')}
          </div>
        ) : (
          <StandsMap
            pins={drivePins}
            selectedIds={EMPTY_SELECTION}
            boundaryAreas={boundaryAreas}
            subAreaPolygons={subAreas}
            forbiddenPolygons={forbiddenPolygons}
            appearance={appearance}
            teamBorderColor={teamBorderColor}
            areaRings={highlightRings}
            areaOuterCount={highlightOuterCount}
            interactive={false}
            style={{ width: '100%', height: '100%' }}
          />
        )}
        <span
          style={{
            position: 'absolute', left: 6, top: 6, width: 20, height: 20, borderRadius: 999,
            background: COLORS.primary, color: COLORS.onPrimary, fontSize: 11, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
          }}
        >
          {index + 1}
        </span>
      </button>

      {/* Details */}
      <button
        onClick={onEdit}
        className="wh-plan-drive-row-details"
        style={{
          display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, justifyContent: 'center',
          background: 'transparent', border: 'none', padding: '2px 0', cursor: 'pointer',
          textAlign: 'left', fontFamily: 'inherit',
        }}
      >
        <div
          style={{
            fontSize: 15, fontWeight: 700, color: nameMissing ? COLORS.error : COLORS.onSurface,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}
        >
          {drive.name.trim() || t('row.untitledDrive')}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 2 }}>
          {baseName && (
            <>
              <span
                style={{
                  fontSize: 12, fontWeight: 600, color: COLORS.onSurface,
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180,
                }}
              >
                <Icon name="map" size={12} color={COLORS.outline} />
                {baseName}
              </span>
              <span style={{ fontSize: 12, color: COLORS.outline }}>·</span>
            </>
          )}
          <span
            style={{
              fontSize: 12, fontWeight: 600,
              color: noStands ? COLORS.error : COLORS.onSurface,
              display: 'inline-flex', alignItems: 'center', gap: 5,
            }}
          >
            <Icon name="target" size={12} color={noStands ? COLORS.error : COLORS.outline} />
            {t('row.standCount', { count: stands.length })}
          </span>
          <span style={{ fontSize: 12, color: COLORS.outline }}>·</span>
          <span style={{ fontSize: 12, color: COLORS.outline }}>{t('row.assignedCount', { count: assignedCount })}</span>
          {assignees.length > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 2 }}>
              {assignees.slice(0, 4).map((a, i) => (
                <span key={a.id} style={{ marginLeft: i === 0 ? 0 : -6, border: `1.5px solid ${COLORS.surfaceContainerLowest}`, borderRadius: 999 }}>
                  <PlanHuntAvatar member={a} size={20} />
                </span>
              ))}
              {assignees.length > 4 && (
                <span style={{ marginLeft: 4, fontSize: 11, color: COLORS.outline }}>+{assignees.length - 4}</span>
              )}
            </span>
          )}
        </div>
      </button>

      {/* Actions */}
      <div className="wh-plan-drive-row-actions" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <button
            className="wh-btn-icon"
            style={{ width: 26, height: 26 }}
            onClick={() => onMove(-1)}
            disabled={index === 0}
            title={t('row.moveUp')}
          >
            <Icon name="chevron-down" size={13} style={{ transform: 'rotate(180deg)' }} />
          </button>
          <button
            className="wh-btn-icon"
            style={{ width: 26, height: 26 }}
            onClick={() => onMove(1)}
            disabled={index === total - 1}
            title={t('row.moveDown')}
          >
            <Icon name="chevron-down" size={13} />
          </button>
        </div>
        <button
          className="wh-btn wh-btn-outline"
          style={{ height: 32, padding: '0 12px', fontSize: 13 }}
          onClick={onEdit}
        >
          <Icon name="map" size={14} />
          {t('row.edit')}
        </button>
        <button className="wh-btn-icon" onClick={onRemove} title={t('row.removeDrive')}>
          <Icon name="trash" size={16} color={COLORS.error} />
        </button>
      </div>
    </div>
  )
}
